<template>
  <div class="post-webwidget-v2-now-playing-image-wrapper">
    <template v-if="imageSrc">
      <!-- D-255 (A9): the fourth v2 image, and the only one of the four that gets a REAL
           `alt` rather than an empty one. The other three were glyphs inside controls
           that `aria-label` now names; this is CONTENT — the show cover or the episode
           artwork — so an empty alt would be a lie about what is on screen. -->
      <img
        v-if="isImageReady"
        :class="imageClassObject"
        :src="imageSrc"
        :width="width"
        :height="height"
        :alt="label"
        @load="onImageLoad"
        @error="onImageLoad"
      />
      <div
        v-else
        class="post-webwidget-v2-now-playing-image-loader"
      >
        Loading image...
      </div>
    </template>
    <template v-else>
      <!-- 🪤 `alt=""` here, and the asymmetry with the branch above is deliberate. This
           is the locally generated "No Image" placeholder, reached only when the
           `episode_images` mod is on and the active episode has no image of its own. It
           carries no information a name could convey, and announcing "No Image" is noise
           in the one place a screen-reader user has nothing to miss. -->
      <img
        :class="imageClassObject"
        :src="noImagePlaceholder"
        :width="width"
        :height="height"
        alt=""
        @load="onImageLoad"
      />
    </template>
  </div>
</template>

<script>
import { placeholderImage } from '../utils/placeholder';

export default {
  name: 'now-playing-image',
  // Named to match v4's `image-loaded` rather than this subtree's
  // `audio-player:ready` style, deliberately: app.vue already listens for
  // `image-loaded` on <v4-player>, and one grep-able name across both versions
  // beats local stylistic consistency for a cross-cutting resize signal.
  emits: ['image-loaded'],
  props: {
    width: Number,
    height: Number,
    compactOnMobile: {
      type: Boolean,
    },
    imageSrc: {
      type: String,
    },
    // D-255 (A9). Supplied by `NowPlayingSection`, which is the only component that
    // knows whether this box is showing the show cover or an episode's own artwork.
    label: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      noImagePlaceholder: placeholderImage(this.width, this.height, 'No Image'), // Same dimensions as episode images.
      isImageReady: false,
      imageClassObject: {
        'post-webwidget-v2-now-playing-image': true,
        'post-webwidget-v2-now-playing-image--compact-on-mobile': this.compactOnMobile,
      },
    };
  },
  methods: {
    // The cover's PIXELS have arrived, so the box has reflowed from the
    // width/height ATTRIBUTE aspect to the image's intrinsic one — measured
    // 187px → 390px at a 390px viewport, i.e. the content grows by ~200px after
    // informResize's 200ms debounce has already posted the pre-cover height.
    // Nothing re-posted, so the iframe stayed short with `scrolling="no"` and the
    // episode list sat below the fold: 186px clipped on the deployed build
    // (#399-class, PRS §6.63). Note this is NOT gated on `isImageReady` — the
    // real <img> is in the DOM with its pixels still pending (complete=false,
    // naturalHeight=0), which is exactly why an element-presence check is not a
    // load signal here. No $nextTick needed: the consumer is the debounced
    // informResize, which measures well after layout settles.
    onImageLoad() {
      this.$emit('image-loaded');
    },

    downloadImage() {
      const image = new Image();
      // Epoch guard: `imageSrc` changes on every episode switch and each change
      // starts another uncancelled decode. Without this, a SLOW preload for a
      // previous episode can resolve after a faster later one and flip
      // readiness for the wrong src.
      this._imageEpoch = (this._imageEpoch || 0) + 1;
      const epoch = this._imageEpoch;
      const settle = () => {
        if (epoch !== this._imageEpoch) return;
        this.isImageReady = true;
      };
      image.src = this.imageSrc;
      if (image.complete) {
        // Found in cache
        settle();
      } else {
        // Not found in cache
        image.onload = settle;
        // A 404 used to leave this stuck on 'Loading image…' forever with no
        // notification at all — live today on the zoneless embeds whose media
        // and cover URLs 404. Render the (broken) img and tell the shell, so the
        // iframe re-fits to whatever actually painted.
        image.onerror = settle;
      }
    },

    // Release the preloader so a decode cannot resolve into a dead component.
    stopPreload() {
      this._imageEpoch = (this._imageEpoch || 0) + 1;
    },
  },
  mounted() {
    this.downloadImage();
  },
  beforeUnmount() {
    this.stopPreload();
  },
  watch: {
    imageSrc() {
      this.downloadImage();
    },
  },
};
</script>


<style lang="scss" scoped>
@import '../assets/variables-v2';
.post-webwidget-v2-now-playing-image-wrapper {
  width: 100%;

  .post-webwidget-v2-now-playing-image {
    width: 100%;
    height: 100%;
    border-radius: 2.5px; // half of $borderRadius

    @media screen and (max-width: $mobileBreakpoint) {
      margin-top: -0.5px; // Border radius margin fix
      border-top-left-radius: $borderRadius;
      border-top-right-radius: $borderRadius;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }

    &--compact-on-mobile {
      border-radius: $borderRadius; // Make sure all sides have a border radius
    }

    &-loader {
      display: flex;
      width: 100%;
      align-items: center;
      justify-content: center;
    }
  }
}
</style>
